import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Footer from "./Footer";
import "./Home.css";

/* --- tiny inline icons (no extra package needed) --- */
const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconSwap = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 1l4 4-4 4" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="M7 23l-4-4 4-4" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconWallet = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
  </svg>
);
const IconBook = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

export default function Home() {
  const pageRef = useRef(null);

  /* scroll-reveal animation */
  useEffect(() => {
    const els = pageRef.current.querySelectorAll(".cl-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("cl-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="cl-page" ref={pageRef}>
      {/* ===================== HEADER ===================== */}
      <header className="cl-header">
        <div className="cl-header__inner">
          <Link to="/" className="cl-brand">
            <span className="cl-brand__mark cl-brand__mark--logo">
              <img
                src="https://jaipuria.edu.in/wp-content/uploads/2021/07/sajes_great_place_logo.jpg"
                alt="Jaipuria Community Library logo"
              />
            </span>
            <span className="cl-brand__text">Community Library<br />Management System</span>
          </Link>
          <nav className="cl-nav">
            <a href="#features" className="cl-nav__link">Features</a>
            <Link to="/login" state={{ showRegister: false }} className="cl-nav__link">Login</Link>
            <Link to="/login" state={{ showRegister: true }} className="cl-btn cl-btn--ghost cl-btn--sm">Register</Link>
          </nav>
        </div>
      </header>

      {/* ===================== HERO ===================== */}
      <section className="cl-hero">
        <div className="cl-hero__inner">
          <div className="cl-hero__copy">
            <span className="cl-eyebrow cl-eyebrow--light cl-fade" style={{ "--d": "0ms" }}>
              Jaipuria Group of Educational Institutions
            </span>
            <h1 className="cl-hero__title cl-fade" style={{ "--d": "120ms" }}>
              The Making<br />Of A Reader
            </h1>
            <p className="cl-hero__lead cl-fade" style={{ "--d": "240ms" }}>
              Everything you need to discover, borrow and manage books — search the
              catalogue, reserve titles, and track your reads, all in one place.
            </p>
            <div className="cl-hero__actions cl-fade" style={{ "--d": "360ms" }}>
              <Link to="/login" state={{ showRegister: true }} className="cl-btn cl-btn--white">Get started</Link>
              <Link to="/login" state={{ showRegister: false }} className="cl-btn cl-btn--outline">Login</Link>
            </div>
          </div>

          {/* ---- Animated 3D book ---- */}
          <div className="cl-hero__art cl-fade" style={{ "--d": "300ms" }} aria-hidden="true">
            <div className="cl-book-scene">
              <div className="cl-book">
                <div className="cl-book__inside">
                  <span className="cl-book__inside-label">A LIBRARY BY</span>
                  <span className="cl-book__emblem"><IconBook /></span>
                  <span className="cl-book__inside-name">JAIPURIA</span>
                </div>
                <div className="cl-book__page cl-book__page--3" />
                <div className="cl-book__page cl-book__page--2" />
                <div className="cl-book__page cl-book__page--1" />
                <div className="cl-book__cover">
                  <span className="cl-book__cover-top">THE</span>
                  <span className="cl-book__cover-title">Community<br />Library</span>
                  <span className="cl-book__cover-sub">Management System</span>
                </div>
              </div>
              <div className="cl-book__shadow" />
            </div>
          </div>
        </div>

        {/* white wave divider */}
        <div className="cl-hero__wave" aria-hidden="true">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,46 C320,120 1040,4 1440,72 L1440,120 L0,120 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      {/* ===================== FEATURES ===================== */}
      <section className="cl-section" id="features">
        <div className="cl-section__head cl-reveal">
          <span className="cl-eyebrow">Everything you need</span>
          <h2>One system for the whole library</h2>
        </div>
        <div className="cl-grid">
          <article className="cl-feature cl-reveal" style={{ "--d": "0ms" }}>
            <span className="cl-feature__icon"><IconSearch /></span>
            <h3>Browse the catalogue</h3>
            <p>Search thousands of titles by author, genre or availability.</p>
          </article>
          <article className="cl-feature cl-reveal" style={{ "--d": "90ms" }}>
            <span className="cl-feature__icon"><IconSwap /></span>
            <h3>Issue &amp; return</h3>
            <p>Borrow online, track due dates and renew with a single tap.</p>
          </article>
          <article className="cl-feature cl-reveal" style={{ "--d": "180ms" }}>
            <span className="cl-feature__icon"><IconUser /></span>
            <h3>Manage your account</h3>
            <p>See your borrowing history, holds and reading lists in one place.</p>
          </article>
          <article className="cl-feature cl-reveal" style={{ "--d": "270ms" }}>
            <span className="cl-feature__icon"><IconWallet /></span>
            <h3>Fines &amp; payments</h3>
            <p>Check pending fines and clear them online in seconds.</p>
          </article>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      {/* No .cl-reveal here on purpose — this is the page's primary
          conversion CTA, so its blue background + colors must always be
          visible immediately rather than depend on a scroll-triggered
          IntersectionObserver firing before the user reaches it. */}
      <section className="cl-cta">
        <div className="cl-cta__inner">
          <h2>Ready to start reading?</h2>
          <p>Join the Jaipuria Community Library and bring the whole catalogue home with you.</p>
          <Link to="/login" state={{ showRegister: true }} className="cl-btn cl-btn--white">Create your account</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
